import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { gsap } from 'gsap';
import { ScrollTrigger, CSSPlugin } from 'gsap/all';
import { ApiClientService } from '../../services/api-client.service';
import { LocationService, UserLocation } from '../../services/location.service';
import { EventData } from '../../../app/shared/models/event.model';
import { catchError, of } from 'rxjs';

let nearbyEventsPluginsRegistered = false;

@Component({
  selector: 'app-nearby-events',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './nearby-events.component.html',
  styleUrl: './nearby-events.component.scss',
})
export class NearbyEventsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('nearbySection', { static: true }) nearbySection!: ElementRef<HTMLElement>;

  private readonly isBrowser: boolean;
  private locationSub!: Subscription;

  events: EventData[] = [];
  loading = false;
  error = '';
  location: UserLocation | null = null;
  readonly placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiM2MzY2ZjEiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiNlYzQ4OTkiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2EpIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5FdmVudDwvdGV4dD48L3N2Zz4=';

  get viewMoreParams(): Record<string, string> {
    return this.location
      ? { city: this.location.city, state: this.location.state, category: 'Events' }
      : { category: 'Events' };
  }

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly apiClient: ApiClientService,
    private readonly locationService: LocationService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser && !nearbyEventsPluginsRegistered) {
      gsap.registerPlugin(ScrollTrigger, CSSPlugin);
      nearbyEventsPluginsRegistered = true;
    }
  }

  ngOnInit(): void {
    this.locationSub = this.locationService.location$.subscribe((loc) => {
      this.location = loc;
      this.loadEvents(loc);
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || window.innerWidth <= 768) return;
    this.initAnimations();
  }

  ngOnDestroy(): void {
    this.locationSub?.unsubscribe();
  }

  loadEvents(loc: UserLocation | null): void {
    this.loading = true;
    this.error = '';

    let endpoint = '/events/public?limit=12';
    if (loc) {
      endpoint += `&lat=${loc.lat}&lng=${loc.lng}&radius=${loc.radius}`;
    }

    this.apiClient.get<EventData[]>(endpoint).pipe(
      catchError(() => {
        this.error = 'Failed to load nearby events. Please try again.';
        return of([]);
      })
    ).subscribe((events) => {
      this.events = events.slice(0, 12);
      this.loading = false;
    });
  }

  formatDate(dateTime: string): string {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  }

  formatPriceRange(priceTiers: { price: number }[]): string {
    if (!priceTiers?.length) return 'Free';
    const prices = priceTiers.map((t) => t.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `$${min.toFixed(2)}` : `$${min.toFixed(2)} – $${max.toFixed(2)}`;
  }

  private initAnimations(): void {
    const el = this.nearbySection.nativeElement;
    gsap.fromTo(el, { y: 50, opacity: 0 }, {
      y: 0, opacity: 1, duration: 1, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' },
    });
    const cards = el.querySelectorAll('.event-card');
    gsap.fromTo(cards, { y: 30, opacity: 0 }, {
      y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 70%', toggleActions: 'play none none reverse' },
    });
  }
}
